import React, { useState } from "react";
import { Modal } from "antd";
import { useForm, Controller } from "react-hook-form";
import Button from "../../../components/button";
import useRequest from "../../../components/hooks/use-request";
import { showToast } from "../../../components/toast";
import { Plus, ChevronUp, ChevronDown, Check } from "lucide-react";
import { CircleLoader } from "react-spinners";

interface ResourcePermissionsType {
  [key: string]: string[];
}

const resourcePermissions: ResourcePermissionsType = {
  sales: ["read", "write", "update", "delete", "approve"],
  inventory: ["read", "write", "update", "delete", "approve"],
  users: ["read", "write", "update", "delete", "approve"],
};

interface RoleFormData {
  name: string;
  permissions: {
    [resource: string]: string[];
  };
}

const CreateRole: React.FC<{ isVisible: boolean; onClose: () => void }> = ({
  isVisible,
  onClose,
}) => {
  const { loading, makeRequest } = useRequest("/roles", "POST");
  const { handleSubmit, control, reset } = useForm<RoleFormData>({
    defaultValues: {
      name: "",
      permissions: {},
    },
  });

  const [expandedSections, setExpandedSections] = useState<{
    [resource: string]: boolean;
  }>({
    sales: true, // Default expanded
    inventory: false,
    customers: false,
    reports: false,
    users: false,
  });

  const toggleSection = (resource: string) => {
    setExpandedSections({
      ...expandedSections,
      [resource]: !expandedSections[resource],
    });
  };
  const isResourceSelected = (
    resource: string,
    permissions: { [resource: string]: string[] }
  ) => {
    return permissions[resource] && permissions[resource].length > 0;
  };

  const toggleResource = (
    resource: string,
    selectedPermissions: { [resource: string]: string[] },
    onChange: Function
  ) => {
    if (isResourceSelected(resource, selectedPermissions)) {
      const newPermissions = { ...selectedPermissions };
      delete newPermissions[resource];
      onChange(newPermissions);
    } else {
      onChange({
        ...selectedPermissions,
        [resource]: ["read", "write", "update", "delete", "approve"],
      });

      setExpandedSections({
        ...expandedSections,
        [resource]: true,
      });
    }
  };

  const togglePermission = (
    resource: string,
    permission: string,
    selectedPermissions: { [resource: string]: string[] },
    onChange: Function
  ) => {
    const resourcePermissions = selectedPermissions[resource] || [];
    let newResourcePermissions;

    if (resourcePermissions.includes(permission)) {
      newResourcePermissions = resourcePermissions.filter(
        (p) => p !== permission
      );
    } else {
      newResourcePermissions = [...resourcePermissions, permission];
    }

    const newPermissions = {
      ...selectedPermissions,
      [resource]: newResourcePermissions,
    };

    if (newPermissions[resource].length === 0) {
      delete newPermissions[resource];
    }

    onChange(newPermissions);
  };

  const formatPermissionsForSubmission = (permissions: {
    [resource: string]: string[];
  }) => {
    return Object.entries(permissions).map(([resource, actions]) => ({
      resource,
      actions,
    }));
  };

  const handleCreateRole = handleSubmit(async (formData) => {
    const hasPermissions = Object.keys(formData.permissions).length > 0;

    if (!hasPermissions) {
      showToast("Please select at least one resource permission", false, {
        position: "top-right",
      });
      return;
    }

    const rolePayload = {
      name: formData.name,
      permissions: formatPermissionsForSubmission(formData.permissions),
    };

    const [response] = await makeRequest(rolePayload);
    if (response.status) {
      showToast(response.message, true, { 
        position: "top-right" 
      });
      reset();
      window.location.reload()
    } else {
      showToast(response?.message || "Role creation failed", false, {
        position: "top-right",
      });
    }
  });

  return (
    <Modal
      title="Create Role"
      open={isVisible}
      onCancel={onClose}
      width="80%"
        centered={true}
        footer={null}
        style={{
          padding: 0,
          margin: 0,
          maxWidth: "1200px",
          height: "auto",
        }}
    >
      <form onSubmit={handleCreateRole}>
        <div className="mb-6">
          <label htmlFor="roleName" className="block mb-2 font-medium">
            Role Name
          </label>
          <Controller
            name="name"
            control={control}
            rules={{ required: "Role name is required" }}
            render={({ field, fieldState: { error } }) => (
              <>
                <input
                  {...field}
                  id="name"
                  className="w-full rounded-full border p-3 outline-none"
                  placeholder="Enter Role Name"
                />
                {error && (
                  <p className="text-red-500 text-sm mt-1">{error.message}</p>
                )}
              </>
            )}
          />
        </div>

        <div className="mb-6">
          <h3 className="mb-3 font-medium">Assign Role Permissions</h3>

          <Controller
            name="permissions"
            control={control}
            render={({ field: { value, onChange } }) => (
              <div className="rounded-lg overflow-hidden">
                {Object.keys(resourcePermissions).map((resource) => (
                  <div key={resource} className="border-b last:border-b-0">
                    {/* Resource Header */}
                    <div
                      className="flex items-center text-[16px] font-semibold justify-between py-3 px-4 cursor-pointer"
                      onClick={() => toggleSection(resource)}
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-[18px] h-[18px] rounded-md mr-3 border flex items-center justify-center
                            ${
                              isResourceSelected(resource, value)
                                ? "bg-green-600 border-green-600"
                                : "border-gray-400"
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleResource(resource, value, onChange);
                          }}
                        >
                          {isResourceSelected(resource, value) && (
                            <Check
                              size={16}
                              className="text-white rounded-xl"
                            />
                          )}
                        </div>
                        <span className="capitalize font-medium">
                          {resource.charAt(0).toUpperCase() + resource.slice(1)}
                        </span>
                      </div>
                      {expandedSections[resource] ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                    </div>

                    {expandedSections[resource] && (
                      <div className="p-3">
                        <div className="grid bg-[#F5F5F5] text-[16px] font-medium grid-cols-5 gap-2 text-center border-b pb-2 mb-2">
                          <div>Read</div>
                          <div>Write</div>
                          <div>Update</div>
                          <div>Delete</div>
                          <div>Approve</div>
                        </div>
                        <div className="grid grid-cols-5 gap-2">
                          {resourcePermissions[resource].map(
                            (permission: any) => (
                              <div
                                key={permission}
                                className="flex justify-center"
                              >
                                <div
                                  className={`w-[18px] h-[18px] rounded-md border flex items-center justify-center cursor-pointer
          ${
            (value[resource] || []).includes(permission)
              ? "bg-green-600 border-green-600"
              : "border-gray-400"
          }`}
                                  onClick={() =>
                                    togglePermission(
                                      resource,
                                      permission,
                                      value,
                                      onChange
                                    )
                                  }
                                >
                                  {(value[resource] || []).includes(
                                    permission
                                  ) && (
                                    <Check size={16} className="text-white" />
                                  )}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          />

          <Controller
            name="permissions"
            control={control}
            render={({ field: { value } }) => (
              <>
                {Object.keys(value).length === 0 && (
                  <p className="text-red-500 text-sm mt-2">
                    Please select at least one resource permission
                  </p>
                )}
              </>
            )}
          />
        </div>

        <div className="flex justify-end mt-6">
          <Button
            size="lg"
            variant="primary"
            type="submit"
            className="font-normal rounded-full"
            disabled={loading}
          >
            {loading ? (
              <CircleLoader color="#ffffff" loading={loading} size={20} />
            ) : (
              <div className="flex items-center gap-2">
                <Plus size={18} /> Create Role
              </div>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateRole;